import { hasFirebaseConfig } from '../config/firebaseConfig';
import { withRetry } from '../core/utils/retry';
import {
  defaultAuthEmail,
  isBareLoginUsername,
  normalizeLoginIdentifier,
} from '../core/auth/loginIdentifier';
import type { OAuthProviderId } from '../core/models/PlatformConfig';
import type { AppUser } from '../types';
import type { AuthCredential } from 'firebase/auth';
import { OAUTH_PROVIDER_FIREBASE_IDS, type MfaResolver } from './authTypes';
import {
  providerLabel,
  refreshAndMapUser,
} from './authHelpers';
import {
  finishPrimarySignIn,
  loadAuth,
  mapFirebaseUser,
  wrapMfaError,
  type MultiFactorResolverFn,
} from './firebaseAuthSession';
import { markEmailMfaGatePending } from './emailMfaSession';

export {
  isAccountExistsError,
  isEmailMfaRequiredError,
  isMfaRequiredError,
  type AccountExistsError,
  type EmailMfaRequiredError,
  type MfaRequiredError,
  type AuthProviderInfo,
  type TotpEnrollmentSession,
  type MfaResolver,
  type SmsEnrollmentSession,
  type SmsMfaChallenge,
  type MfaStatus,
} from './authTypes';

export {
  getMfaStatus,
  isTotpEnrollmentSupported,
  startTotpEnrollment,
  finishTotpEnrollment,
  startSmsEnrollment,
  finishSmsEnrollment,
  unenrollTotpMfa,
  unenrollSmsMfa,
  startSmsMfaSignIn,
  resolveMfaSignIn,
  resolveTotpMfaSignIn,
  resolveSmsMfaSignIn,
} from './FirebaseMfaService';

export {
  getEmailMfaStatus,
  sendEmailMfaCode,
  verifyEmailMfaEnrollment,
  verifyEmailMfaSignIn,
  disableEmailMfa,
} from './EmailMfaService';

export {
  signInWithOAuth,
  signInWithGoogle,
  completeGoogleRedirect,
  signInAndLinkGoogle,
  linkGoogleAccount,
  unlinkGoogleAccount,
  getLinkedProviders,
  linkPendingOAuthCredential,
} from './firebaseOAuthSignIn';

export { mapFirebaseUser } from './firebaseAuthSession';

/** True when Firebase Auth SDK can run (independent of mock vs firebase data adapter). */
export function usesFirebaseAuth() {
  return hasFirebaseConfig();
}

/** Sync helper: full email as-is, bare username → `@VITE_AUTH_EMAIL_DOMAIN`. */
export function normalizeAuthEmail(username: string) {
  const value = normalizeLoginIdentifier(username);
  if (!value) return value;
  if (!isBareLoginUsername(value)) return value;
  const domain = String(import.meta.env.VITE_AUTH_EMAIL_DOMAIN || '').trim();
  if (!domain) {
    throw new Error('VITE_AUTH_EMAIL_DOMAIN is required for username-only sign-in.');
  }
  return defaultAuthEmail(value, domain);
}

/**
 * Resolve login identity for Firebase Auth.
 * Bare usernames look up the provisioned email (e.g. joseito → joseito@gmail.com).
 */
export async function resolveAuthEmail(username: string): Promise<string> {
  const value = normalizeLoginIdentifier(username);
  if (!value) {
    throw Object.assign(new Error('Username required'), { code: 'auth/invalid-email' });
  }
  if (!isBareLoginUsername(value)) return value;

  if (usesFirebaseAuth()) {
    try {
      const { resolveLoginEmail } = await import('./firebase/FunctionsService.js');
      const result = await resolveLoginEmail({ username: value }) as {
        email?: string;
        ambiguous?: boolean;
      };
      if (result.ambiguous) {
        throw Object.assign(
          new Error('Multiple accounts match that username. Use your full email.'),
          { code: 'auth/login-ambiguous' },
        );
      }
      const email = String(result.email ?? '').trim().toLowerCase();
      if (email.includes('@')) return email;
    } catch (err) {
      const code = String((err as { code?: string })?.code ?? '');
      if (code === 'auth/login-ambiguous' || code.endsWith('login-ambiguous')) throw err;
      // Callable unavailable → fall back to platform domain append.
    }
  }

  return normalizeAuthEmail(value);
}

export async function signInWithCredentials(username: string, password: string) {
  const { auth, signInWithEmailAndPassword, getMultiFactorResolver } = await loadAuth();
  const email = await resolveAuthEmail(username);
  const signInOnce = () => signInWithEmailAndPassword(auth, email, password);
  try {
    await withRetry(signInOnce, { attempts: 3, baseDelayMs: 500 });
    // Synchronous: close the onAuthStateChanged race before any further awaits.
    const uid = auth.currentUser?.uid?.trim();
    if (uid) markEmailMfaGatePending(uid);
    return finishPrimarySignIn(email);
  } catch (err) {
    const { retryNativeSignInAfterNetworkFailure } = await import('./authNetworkRetry.js');
    const retried = await retryNativeSignInAfterNetworkFailure({
      err,
      auth,
      email,
      signInOnce,
      getMultiFactorResolver: getMultiFactorResolver as MultiFactorResolverFn,
    });
    if (retried) return retried;
    wrapMfaError(err, auth, getMultiFactorResolver as MultiFactorResolverFn);
  }
}

/** Completes passwordless passkey unlock after a verified WebAuthn assertion. */
export async function signInWithPasskeyToken(customToken: string, email: string) {
  const { auth, signInWithCustomToken, getMultiFactorResolver } = await loadAuth();
  try {
    await withRetry(
      () => signInWithCustomToken(auth, customToken),
      { attempts: 3, baseDelayMs: 500 },
    );
    const uid = auth.currentUser?.uid?.trim();
    if (uid) markEmailMfaGatePending(uid);
    return finishPrimarySignIn(email);
  } catch (err) {
    wrapMfaError(err, auth, getMultiFactorResolver as MultiFactorResolverFn);
  }
}

export async function completeMfaSignIn(
  resolver: MfaResolver,
  verificationCode: string,
  pendingOAuthCredential?: AuthCredential | null,
) {
  const { resolveMfaSignIn } = await import('./FirebaseMfaService');
  const { linkPendingOAuthCredential } = await import('./firebaseOAuthSignIn');
  const { auth } = await loadAuth();
  await resolveMfaSignIn(resolver, verificationCode);
  if (pendingOAuthCredential) {
    await linkPendingOAuthCredential(pendingOAuthCredential);
  }
  const email = auth.currentUser?.email ?? '';
  return finishPrimarySignIn(email);
}

export async function completeSmsMfaSignIn(
  resolver: MfaResolver,
  verificationId: string,
  verificationCode: string,
  pendingOAuthCredential?: AuthCredential | null,
) {
  const { resolveSmsMfaSignIn } = await import('./FirebaseMfaService');
  const { linkPendingOAuthCredential } = await import('./firebaseOAuthSignIn');
  const { auth } = await loadAuth();
  await resolveSmsMfaSignIn(resolver, verificationId, verificationCode);
  if (pendingOAuthCredential) {
    await linkPendingOAuthCredential(pendingOAuthCredential);
  }
  const email = auth.currentUser?.email ?? '';
  return finishPrimarySignIn(email);
}

export async function completeEmailMfaSignIn(code: string) {
  const { verifyEmailMfaSignIn } = await import('./EmailMfaService');
  const { auth } = await loadAuth();
  await verifyEmailMfaSignIn(code);
  const uid = auth.currentUser?.uid;
  if (uid) {
    const { markEmailMfaVerified } = await import('./emailMfaSession');
    markEmailMfaVerified(uid);
  }
  const { syncProfileAfterSignIn } = await import('./authHelpers');
  return syncProfileAfterSignIn(mapFirebaseUser, auth);
}

export function oauthProviderLabel(providerId: OAuthProviderId): string {
  return providerLabel(OAUTH_PROVIDER_FIREBASE_IDS[providerId]);
}

export async function refreshAuthUser() {
  const { auth } = await loadAuth();
  return refreshAndMapUser(mapFirebaseUser, auth);
}

export function subscribeToAuthState(callback: (user: AppUser | null) => void | Promise<void>) {
  let unsub = () => {};

  import('../config/firebase.js').then(({ auth }) =>
    import('firebase/auth').then(({ onAuthStateChanged }) => {
      unsub = onAuthStateChanged(auth, async (fbUser) => {
        if (!fbUser) {
          const { cancelAuthRestore } = await import('./authRestore.js');
          cancelAuthRestore();
          await callback(null);
          return;
        }
        const { applyAuthStateUser } = await import('./authRestore.js');
        await applyAuthStateUser(auth, fbUser, callback);
      });
    }),
  );

  return () => unsub();
}

export async function signOutUser() {
  try {
    const { clearDeviceSession } = await import('./devicePasskeys.js');
    clearDeviceSession();
  } catch {
    // ignore
  }
  try {
    const { clearEmailMfaSession } = await import('./emailMfaSession.js');
    clearEmailMfaSession();
  } catch {
    // ignore
  }
  const { auth, signOut } = await loadAuth();
  await signOut(auth);
}

/** Re-authenticates the current Firebase user with their password (identity check). */
export async function reauthenticateWithPassword(password: string) {
  const { auth, EmailAuthProvider, reauthenticateWithCredential } = await loadAuth();
  const user = auth.currentUser;
  if (!user?.email) {
    throw Object.assign(new Error('No signed-in user to reauthenticate.'), {
      code: 'auth/no-current-user',
    });
  }
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
}

/**
 * Updates Auth displayName and mirrors `users/{uid}.name` when a profile doc exists.
 * Ghost god accounts (Auth-only) only update Auth.
 */
export async function updateOwnDisplayName(rawName: string): Promise<AppUser> {
  const { normalizeDisplayName } = await import('../core/rules/displayName');
  const name = normalizeDisplayName(rawName);
  const { auth, updateProfile } = await loadAuth();
  const user = auth.currentUser;
  if (!user) {
    throw Object.assign(new Error('Not signed in'), { code: 'auth/no-current-user' });
  }
  await updateProfile(user, { displayName: name });
  try {
    const { doc, getDoc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../config/firebase.js');
    const ref = doc(db, 'users', user.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, { name, updatedAt: serverTimestamp() });
    }
  } catch (err) {
    console.warn('[Auth] Profile name Firestore mirror skipped:', err);
  }
  await user.reload();
  const mapped = await refreshAndMapUser(mapFirebaseUser, auth);
  if (!mapped) {
    throw Object.assign(new Error('Not signed in'), { code: 'auth/no-current-user' });
  }
  return mapped;
}

export { providerLabel };
